import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/UI/Button/Button';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { createPortal } from 'react-dom';
import { OnlineBookingPricingOptions } from 'types/OnlineBooking';

import classes from './AddonsModeFooter.module.scss';

interface AddonsModeFooterProps {
    total: number;
    label: string;
    loading?: boolean;
    disabled?: boolean;
    hint?: string;
    // Summary for small screens, where the right column only keeps this footer.
    mobileSummary?: React.ReactNode;
    onSubmit: () => void;
}

/** "Starting at $X" + CTA (Figma 1930:6228), visible on every screen size. */
export const AddonsModeFooter: React.FC<AddonsModeFooterProps> = ({
    total,
    label,
    loading,
    disabled,
    hint,
    mobileSummary,
    onSubmit
}) => {
    const { onlineBookingPreferences } = useMarketplace();
    const [isSummaryOpen, setIsSummaryOpen] = React.useState(false);

    return (
        <div className={classes.root}>
            <div className={classes.total}>
                <span>
                    {onlineBookingPreferences.pricingOption === OnlineBookingPricingOptions.Price
                        ? 'Price'
                        : 'Starting at'}
                </span>

                <span className={classes.amount}>
                    {mobileSummary && (
                        <Button
                            variant="link"
                            size="small"
                            className={classes.summaryLink}
                            onClick={() => setIsSummaryOpen(true)}
                        >
                            View summary
                        </Button>
                    )}
                    ${total.toFixed(2)}
                </span>
            </div>

            {hint && <p className={classes.hint}>{hint}</p>}

            <Button fullWidth disabled={disabled} loading={loading} onClick={onSubmit}>
                {label}
            </Button>

            {mobileSummary &&
                isSummaryOpen &&
                createPortal(
                    <div
                        className={classes.overlay}
                        onClick={event => {
                            if (event.target === event.currentTarget) {
                                setIsSummaryOpen(false);
                            }
                        }}
                    >
                        <div
                            className={classes.sheet}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Summary"
                        >
                            <div className={classes.sheetHeader}>
                                <p className={classes.sheetTitle}>Summary</p>
                                <button
                                    type="button"
                                    className={classes.close}
                                    aria-label="Close summary"
                                    onClick={() => setIsSummaryOpen(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <div className={classes.sheetBody}>{mobileSummary}</div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};
